/**
 * RevealController — Opacity-lerp controller for fade in/out of 3D assets.
 *
 * Manages material opacity across all meshes of an Object3D scene with:
 * - Per-material snapshot of original opacity / transparent / depthWrite
 * - Glass-safe: fades to original opacity (not always 1.0)
 * - Post-fade cleanup: restores `transparent=false` + `depthWrite=true`
 *   on originally-opaque materials to prevent permanent sorting artifacts
 *
 * @see docs/ACTIVE_TASK.md — Teil 3: Reveal
 */

import type { RevealOptions } from '../api/types/objectManager';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Snapshot of a material's original transparency-related properties. */
interface MaterialSnapshot {
  opacity: number;
  transparent: boolean;
  depthWrite: boolean;
}

/**
 * Minimal material interface — avoids coupling to Three.js concrete types.
 * Works with MeshStandardMaterial, MeshPhysicalMaterial, etc.
 */
export interface RevealMaterial {
  opacity: number;
  transparent: boolean;
  depthWrite: boolean;
  needsUpdate: boolean;
}

/** Minimal mesh-like interface for traversal. */
export interface RevealMesh {
  isMesh?: boolean;
  material?: RevealMaterial | RevealMaterial[];
}

/** Minimal Object3D-like interface. */
export interface RevealObject {
  traverse(callback: (child: RevealMesh) => void): void;
  visible: boolean;
}

// ---------------------------------------------------------------------------
// Easing
// ---------------------------------------------------------------------------

/** Ease-in-out cubic (smooth start + end). */
function easeInOutCubic(t: number): number {
  return t < 0.5
    ? 4 * t * t * t
    : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** Linear (no easing). */
function linear(t: number): number {
  return t;
}

function getEasing(name: 'linear' | 'ease-in-out'): (t: number) => number {
  return name === 'linear' ? linear : easeInOutCubic;
}

// ---------------------------------------------------------------------------
// RevealController
// ---------------------------------------------------------------------------

const DEFAULT_DURATION = 600;

export class RevealController {
  /** Map from material instance → original snapshot. */
  private _snapshots = new Map<RevealMaterial, MaterialSnapshot>();
  /** All unique materials collected from the scene. */
  private _materials: RevealMaterial[] = [];

  // Animation state
  private _factor = 1;          // Current blend factor (0 = invisible, 1 = fully visible)
  private _targetFactor = 1;    // Where we're heading
  private _startFactor = 1;     // Where we started from
  private _duration = DEFAULT_DURATION;
  private _elapsed = 0;
  private _active = false;
  private _easingFn: (t: number) => number = easeInOutCubic;
  private _onComplete: (() => void) | null = null;
  private _disposed = false;

  /**
   * Attach to a scene graph — traverses and snapshots all mesh materials.
   * Call once after cloning the GLTF scene.
   */
  attach(scene: RevealObject): void {
    this._snapshots.clear();
    this._materials = [];

    scene.traverse((child) => {
      if (!child.isMesh || !child.material) return;

      const mats = Array.isArray(child.material)
        ? child.material
        : [child.material];

      for (const mat of mats) {
        if (this._snapshots.has(mat)) continue; // shared material — already captured
        this._snapshots.set(mat, {
          opacity: mat.opacity,
          transparent: mat.transparent,
          depthWrite: mat.depthWrite,
        });
        this._materials.push(mat);
      }
    });
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Fade in: 0 → 1. Each material's final opacity = its original opacity.
   * If not animated, instantly jumps to fully visible.
   */
  fadeIn(options: RevealOptions = {}): void {
    if (this._disposed) return;
    const animated = options.animated ?? true;
    const duration = options.duration ?? DEFAULT_DURATION;

    if (!animated) {
      this._setFactorImmediate(1);
      options.onComplete?.();
      return;
    }

    this._startAnimation(1, duration, options.easing ?? 'ease-in-out', options.onComplete ?? null);
  }

  /**
   * Fade out: 1 → 0. Each material's opacity lerps toward 0.
   * If not animated, instantly jumps to invisible.
   */
  fadeOut(options: RevealOptions = {}): void {
    if (this._disposed) return;
    const animated = options.animated ?? true;
    const duration = options.duration ?? DEFAULT_DURATION;

    if (!animated) {
      this._setFactorImmediate(0);
      options.onComplete?.();
      return;
    }

    this._startAnimation(0, duration, options.easing ?? 'ease-in-out', options.onComplete ?? null);
  }

  /**
   * Per-frame update. Call from useFrame or a tick loop.
   * @param deltaMs — elapsed time since last frame in **milliseconds**.
   * @returns `true` while an animation is active.
   */
  tick(deltaMs: number): boolean {
    if (!this._active || this._disposed) return false;

    this._elapsed += deltaMs;
    const t = Math.min(this._elapsed / this._duration, 1);
    const eased = this._easingFn(t);

    this._factor = this._startFactor + (this._targetFactor - this._startFactor) * eased;
    this._applyFactor(this._factor);

    if (t >= 1) {
      this._active = false;
      this._factor = this._targetFactor;
      this._applyFactor(this._factor);
      this._finalizeMaterials();
      const cb = this._onComplete;
      this._onComplete = null;
      cb?.();
    }

    return this._active;
  }

  /**
   * Instantly set the blend factor (0 = invisible, 1 = fully visible).
   * Skips any running animation.
   */
  setImmediate(factor: number): void {
    if (this._disposed) return;
    this._setFactorImmediate(factor);
  }

  /** Whether a fade animation is currently in progress. */
  get isAnimating(): boolean {
    return this._active;
  }

  /** Current blend factor (0–1). */
  get factor(): number {
    return this._factor;
  }

  /** Number of tracked materials. */
  get materialCount(): number {
    return this._materials.length;
  }

  /**
   * Dispose: restore all original material properties and release references.
   * Safe to call multiple times.
   */
  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    this._active = false;
    this._onComplete = null;

    // Restore originals
    for (const mat of this._materials) {
      const snap = this._snapshots.get(mat);
      if (!snap) continue;
      mat.opacity = snap.opacity;
      mat.transparent = snap.transparent;
      mat.depthWrite = snap.depthWrite;
      mat.needsUpdate = true;
    }

    this._snapshots.clear();
    this._materials = [];
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  private _startAnimation(
    target: number,
    duration: number,
    easing: 'linear' | 'ease-in-out',
    onComplete: (() => void) | null,
  ): void {
    this._startFactor = this._factor;
    this._targetFactor = target;
    this._duration = Math.max(duration, 1); // avoid divide-by-zero
    this._elapsed = 0;
    this._easingFn = getEasing(easing);
    this._onComplete = onComplete;
    this._active = true;

    // Prepare materials for transparency during animation
    this._prepareMaterialsForFade();
  }

  /**
   * Before a fade animation begins, set all materials to transparent mode
   * so opacity changes are visible during the transition.
   */
  private _prepareMaterialsForFade(): void {
    for (const mat of this._materials) {
      mat.transparent = true;
      mat.depthWrite = false;
      mat.needsUpdate = true;
    }
  }

  /**
   * After a fade animation completes, restore material properties to avoid
   * permanent sorting artifacts.
   *
   * - Factor = 1 (fully visible): restore original `transparent` and `depthWrite`
   *   on materials that were originally opaque. Leaves glass/transparent materials
   *   in their original state.
   * - Factor = 0 (fully hidden): keep transparent=true (object is invisible anyway,
   *   will be removed from scene by ObjectManager).
   */
  private _finalizeMaterials(): void {
    if (this._factor >= 1) {
      // Restore originals — critical for sorting correctness
      for (const mat of this._materials) {
        const snap = this._snapshots.get(mat);
        if (!snap) continue;
        mat.transparent = snap.transparent;
        mat.depthWrite = snap.depthWrite;
        mat.needsUpdate = true;
      }
    }
    // Factor = 0: leave as is — materials stay transparent (object will be hidden)
  }

  /**
   * Apply blend factor to all materials.
   * Each material's opacity = originalOpacity × factor.
   *
   * NOTE: needsUpdate is NOT set here — Three.js tracks uniform changes
   * (opacity) automatically. Setting needsUpdate bumps the material version
   * which triggers a shader-recompile check (~720 unnecessary checks during
   * a 600ms fade with 20 materials). needsUpdate is only needed for
   * structural changes (transparent, depthWrite) in _prepareMaterialsForFade
   * and _finalizeMaterials.
   */
  private _applyFactor(factor: number): void {
    for (const mat of this._materials) {
      const snap = this._snapshots.get(mat);
      if (!snap) continue;
      mat.opacity = snap.opacity * factor;
    }
  }

  /** Immediately set factor, apply, and finalize. */
  private _setFactorImmediate(factor: number): void {
    this._active = false;
    this._factor = factor;
    this._targetFactor = factor;
    this._startFactor = factor;

    if (factor < 1) {
      this._prepareMaterialsForFade();
    }
    this._applyFactor(factor);
    if (factor >= 1) {
      this._finalizeMaterials();
    }
  }
}
